export interface SeoFaqItem {
  q: string;
  a: string;
}

export const PUBLIC_FAQ_ITEMS: SeoFaqItem[] = [
  {
    q: "How do I create an account?",
    a: "Click 'Sign Up' in the navigation bar. You'll need to provide your business information, NPI number, and DEA number for verification. Our team will review your application and activate your account.",
  },
  {
    q: "How does pricing work?",
    a: "Browse estimated prices in our public catalog. Estimates are not order quotes. Create an account to view your pricing after verification, or sign in if you already have an approved account. Contact our sales team for volume discounts and custom pricing arrangements.",
  },
  {
    q: "What is DSCSA compliance?",
    a: "The Drug Supply Chain Security Act (DSCSA) is a federal law that requires tracking of pharmaceutical products through the supply chain. Premier Pharma is fully DSCSA compliant, providing complete transaction documentation for all shipments.",
  },
  {
    q: "What categories of products do you carry?",
    a: "We carry Pharmaceuticals, Animal Health products, Medical/Surgical supplies, Caps & Tabs, and Diabetic Supplies. Browse our full catalog in the Products section.",
  },
  {
    q: "How do I place an order?",
    a: "Once your account is verified, browse our product catalog, add items to your cart, and proceed to checkout. You can include a PO number and shipping address with your order.",
  },
  {
    q: "What software solutions do you offer?",
    a: "We offer ScriptTrack, a comprehensive platform for in-office dispensing, DSCSA compliance, inventory management, and e-prescribing. Visit our Software page for pricing and details.",
  },
];

export interface CategoryFaqEnrichment {
  topGenericNames?: string[];
  topStrength?: string | null;
  topManufacturers?: { name: string }[];
}

export interface ManufacturerFaqEnrichment {
  topGenericNames?: string[];
  topCategories?: { name: string }[];
}

export function buildCategoryFaq(
  name: string,
  total: number,
  enrichment?: CategoryFaqEnrichment,
): SeoFaqItem[] {
  const nameLC = name.toLowerCase();
  const genericList = enrichment?.topGenericNames?.filter(Boolean) ?? [];
  const genericMention = genericList.length > 0 ? ` including ${genericList.slice(0, 3).join(", ")}` : "";
  const mfrList = enrichment?.topManufacturers?.map((m) => m.name).filter(Boolean) ?? [];
  const mfrMention =
    mfrList.length > 0
      ? ` Available from top manufacturers including ${mfrList.slice(0, 3).join(", ")}.`
      : "";
  const strengthMention = enrichment?.topStrength
    ? ` The most commonly stocked strength is ${enrichment.topStrength}.`
    : "";

  return [
    {
      q: `Where can I buy wholesale ${nameLC} for my pharmacy?`,
      a: `Premier Pharma stocks ${total.toLocaleString()}+ ${nameLC} products${genericMention} at competitive wholesale pricing for licensed pharmacies, clinics, and healthcare facilities across the United States.${mfrMention}${strengthMention} Browse the catalog above or create a free account to see live pricing.`,
    },
    {
      q: `Is Premier Pharma a licensed ${nameLC} distributor?`,
      a: `Yes. Premier Pharma is an NABP-accredited pharmaceutical distributor and operates in full compliance with the Drug Supply Chain Security Act (DSCSA). Every shipment includes the required transaction information, history, and statement.`,
    },
    {
      q: `How quickly do ${nameLC} orders ship?`,
      a: `In-stock items ship the same business day when ordered before our cutoff. Items not currently in stock can usually be sourced and delivered within two business days through our partner network — just request 2-day shipping from the product page.`,
    },
    {
      q: `Do I need an account to see ${nameLC} pricing?`,
      a: `You can browse estimated prices in the public catalog without an account. Estimates are not order quotes. Create an account and submit your licensing information for review to view your pricing after verification, or sign in to an existing approved account.`,
    },
  ];
}

export function buildManufacturerFaq(
  name: string,
  total: number,
  enrichment?: ManufacturerFaqEnrichment,
): SeoFaqItem[] {
  const genericList = enrichment?.topGenericNames?.filter(Boolean) ?? [];
  const genericMention =
    genericList.length > 0
      ? ` Our ${name} catalog includes ${genericList.slice(0, 3).join(", ")}, and more.`
      : "";
  const catList = enrichment?.topCategories?.map((c) => c.name).filter(Boolean) ?? [];
  const catMention = catList.length > 0 ? ` Top categories include ${catList.slice(0, 3).join(", ")}.` : "";

  return [
    {
      q: `Is Premier Pharma an authorized ${name} wholesaler?`,
      a: `Premier Pharma is a licensed pharmaceutical distributor sourcing ${name} products through authorized channels. We currently carry ${total.toLocaleString()} ${name} SKUs available for wholesale to licensed pharmacies, clinics, doctor's offices, and veterinary practices.${genericMention}`,
    },
    {
      q: `How can I buy ${name} products at wholesale?`,
      a: `Create a free Premier Pharma account, submit your pharmacy or clinic credentials, and you will be approved within minutes. Once verified, all ${name} wholesale pricing becomes visible across the catalog.${catMention}`,
    },
    {
      q: `Are ${name} shipments DSCSA compliant?`,
      a: `Every ${name} order shipped from Premier Pharma includes full DSCSA T2 documentation — Transaction Information (TI) and Transaction Statement (TS) — and is traceable through our EPCIS-compatible inbox. Under EPCIS interoperability, Transaction History is eliminated, and we provide everything your pharmacy needs for full compliance.`,
    },
    {
      q: `What if a ${name} product I need isn't listed?`,
      a: `Email or chat with our sales team — we can typically source any ${name} SKU through our distribution network and have it on the way to you within two business days.`,
    },
  ];
}